import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCoupons = createAsyncThunk(
  "coupons/fetchCoupons",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/coupons", { params });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch coupons");
    }
  }
);

export const createCoupon = createAsyncThunk(
  "coupons/createCoupon",
  async (couponData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/coupons", couponData);
      dispatch(showToast({ message: "Coupon created successfully", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to create coupon", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateCoupon = createAsyncThunk(
  "coupons/updateCoupon",
  async ({ couponId, ...couponData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/superAdmin/coupons/${couponId}`, couponData);
      dispatch(showToast({ message: "Coupon updated successfully", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to update coupon", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const deleteCoupon = createAsyncThunk(
  "coupons/deleteCoupon",
  async (couponId, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/superAdmin/coupons/${couponId}`);
      dispatch(showToast({ message: "Coupon deleted successfully", variant: "success" }));
      return couponId;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to delete coupon", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const toggleCouponStatus = createAsyncThunk(
  "coupons/toggleCouponStatus",
  async (couponId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/superAdmin/coupons/${couponId}/toggle`);
      dispatch(showToast({ message: response.data.message, variant: "success" }));
      return response.data.coupon;
    } catch (err) {
      dispatch(showToast({ message: "Failed to toggle coupon status", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchGlobalSettings = createAsyncThunk(
  "coupons/fetchGlobalSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/settings");
      return response.data.settings;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch settings");
    }
  }
);

export const updateGlobalSettings = createAsyncThunk(
  "coupons/updateGlobalSettings",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put("/superAdmin/settings", payload);
      dispatch(showToast({ message: "Settings saved", variant: "success" }));
      return response.data.settings;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to save settings", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const couponSlice = createSlice({
  name: "coupons",
  initialState: {
    coupons: [],
    total: 0,
    totalPages: 1,
    loading: false,
    actionLoading: false,
    error: null,
    filters: { page: 1, limit: 10, discountType: "", status: "active", search: "", isRetentionCoupon: "" },
    // Global settings
    globalSettings: { retentionDiscountPercent: 30 },
    settingsLoading: false,
    settingsSaving: false,
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = action.payload.coupons;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchCoupons.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createCoupon.pending, (state) => { state.actionLoading = true; })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.coupons.unshift(action.payload.coupon);
        state.total += 1;
      })
      .addCase(createCoupon.rejected, (state) => { state.actionLoading = false; })

      .addCase(updateCoupon.pending, (state) => { state.actionLoading = true; })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.actionLoading = false;
        const idx = state.coupons.findIndex((c) => c._id === action.payload.coupon._id);
        if (idx !== -1) state.coupons[idx] = action.payload.coupon;
      })
      .addCase(updateCoupon.rejected, (state) => { state.actionLoading = false; })

      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.coupons = state.coupons.filter((c) => c._id !== action.payload);
        state.total -= 1;
      })

      .addCase(toggleCouponStatus.fulfilled, (state, action) => {
        const idx = state.coupons.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.coupons[idx].isActive = action.payload.isActive;
      })

      // Global settings
      .addCase(fetchGlobalSettings.pending, (state) => { state.settingsLoading = true; })
      .addCase(fetchGlobalSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.globalSettings = action.payload;
      })
      .addCase(fetchGlobalSettings.rejected, (state) => { state.settingsLoading = false; })
      .addCase(updateGlobalSettings.pending, (state) => { state.settingsSaving = true; })
      .addCase(updateGlobalSettings.fulfilled, (state, action) => {
        state.settingsSaving = false;
        state.globalSettings = action.payload;
      })
      .addCase(updateGlobalSettings.rejected, (state) => { state.settingsSaving = false; });
  },
});

export const { setFilters, clearError } = couponSlice.actions;
export default couponSlice.reducer;
